import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import { Button, Badge, Card, Input, Select, Icon } from '../components/ui';
import { IconArrowLeft, IconTruck, IconPhone, IconMail, IconSync, IconFileSpreadsheet, IconPrinter, IconFilter, IconX, IconInbox, IconFileInvoice, IconMoneybag, IconPercent } from '@tabler/icons-react';

const fmt = (n) => Number(n || 0).toFixed(3);
const formatCurrency = (n) => new Intl.NumberFormat('fr-TN', { style: 'currency', currency: 'TND' }).format(n || 0);

const TYPE_LABELS = {
  INVOICE: { label: 'Facture', variant: 'gray' },
  PAYMENT: { label: 'Règlement', variant: 'success' },
  RS: { label: 'Retenue RS', variant: 'warning' },
};

const STATUS_COLORS = {
  PAID: 'success',
  PARTIALLY_PAID: 'warning',
  VALIDATED: 'info',
  DRAFT: 'gray',
  POSTED: 'success',
  CANCELED: 'danger',
};

export default function SupplierStatement() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '', type: '' });
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { loadStatement(); }, [id]);

  const loadStatement = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await api.get(`/purchase-invoices/${id}/statement`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.error || e?.message || 'Erreur chargement');
    } finally {
      setLoading(false);
    }
  };

  const filteredMovements = useMemo(() => {
    if (!data?.movements) return [];
    return data.movements.filter(m => {
      if (filters.type && m.type !== filters.type) return false;
      if (filters.dateFrom && m.date < filters.dateFrom) return false;
      if (filters.dateTo && m.date > filters.dateTo) return false;
      return true;
    });
  }, [data, filters]);


  const handleExportExcel = () => {
    const { supplier, summary } = data;
    const rows = [
      ['État Détaillé Fournisseur - ' + supplier.name],
      ['Matricule Fiscal', supplier.mf || '—'],
      ['Téléphone', supplier.phone || '—'],
      ['Email', supplier.email || '—'],
      ['Période', filters.dateFrom || 'Début' , '→', filters.dateTo || 'Fin'],
      ['Généré le', new Date().toLocaleDateString('fr-FR')],
      [],
      ['RÉSUMÉ'],
      ['Total Factures (DT)', summary.totalInvoices],
      ['Total Règlements (DT)', summary.totalPayments],
      ['Total RS (DT)', summary.totalRS],
      ['Solde Dû (DT)', summary.solde],
      [],
      ['Date', 'Type', 'Référence', 'Libellé', 'Statut', 'Débit (DT)', 'Crédit (DT)', 'Solde Cumulé (DT)'],
      ...filteredMovements.map(m => [
        m.date || '',
        TYPE_LABELS[m.type]?.label || m.type,
        m.reference,
        m.label,
        m.status || '',
        m.debit > 0 ? m.debit : '',
        m.credit > 0 ? m.credit : '',
        m.balance,
      ]),
    ];

    const csvContent = rows.map(row =>
      Array.isArray(row) ? row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(';') : ''
    ).join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `etat_fournisseur_${data.supplier.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const { supplier, summary } = data;
    const w = window.open('', '_blank');
    w.document.write(`<!DOCTYPE html><html><head><title>État Fournisseur - ${supplier.name}</title><style>
      body{font-family:Arial,sans-serif;margin:30px;font-size:13px}
      h1{color:#1e40af;border-bottom:2px solid #1e40af;padding-bottom:8px;font-size:20px}
      .info{background:#f8fafc;padding:12px;border-radius:6px;margin:12px 0;display:flex;gap:40px}
      .info div{font-size:12px;color:#64748b} .info strong{color:#1e293b;font-size:13px}
      .stats{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin:16px 0}
      .stat{padding:12px;border-radius:6px;text-align:center}
      .stat-label{font-size:11px;color:#64748b;margin-bottom:4px}
      .stat-value{font-size:15px;font-weight:bold}
      table{width:100%;border-collapse:collapse;margin-top:16px}
      th{background:#1e40af;color:white;padding:8px 10px;text-align:left;font-size:11px}
      td{padding:7px 10px;border-bottom:1px solid #e2e8f0;font-size:12px}
      tr:nth-child(even){background:#f8fafc}
      .debit{color:#dc2626;font-weight:bold}
      .credit{color:#16a34a;font-weight:bold}
      .balance-pos{color:#dc2626;font-weight:bold}
      .balance-neg{color:#16a34a;font-weight:bold}
      .footer{margin-top:20px;text-align:right;font-size:11px;color:#94a3b8}
    </style></head><body>
      <h1><i></i> ÉTAT DÉTAILLÉ FOURNISSEUR</h1>
      <div class="info">
        <div><div class="stat-label">Fournisseur</div><strong>${supplier.name}</strong></div>
        ${supplier.mf ? `<div><div class="stat-label">Matricule fiscal</div><strong>${supplier.mf}</strong></div>` : ''}
        ${supplier.phone ? `<div><div class="stat-label">Téléphone</div><strong>${supplier.phone}</strong></div>` : ''}
        ${supplier.email ? `<div><div class="stat-label">Email</div><strong>${supplier.email}</strong></div>` : ''}
        <div><div class="stat-label">Période</div><strong>${filters.dateFrom || 'Début'} → ${filters.dateTo || 'Fin'}</strong></div>
        <div><div class="stat-label">Généré le</div><strong>${new Date().toLocaleDateString('fr-FR')}</strong></div>
      </div>
      <div class="stats">
        <div class="stat" style="background:#fef2f2"><div class="stat-label">Total factures</div><div class="stat-value" style="color:#dc2626">${formatCurrency(summary.totalInvoices)}</div></div>
        <div class="stat" style="background:#f0fdf4"><div class="stat-label">Total règlements</div><div class="stat-value" style="color:#16a34a">${formatCurrency(summary.totalPayments)}</div></div>
        <div class="stat" style="background:#faf5ff"><div class="stat-label">Total RS</div><div class="stat-value" style="color:#7c3aed">${formatCurrency(summary.totalRS)}</div></div>
        <div class="stat" style="background:#eff6ff"><div class="stat-label">Solde dû</div><div class="stat-value" style="color:#1e40af">${formatCurrency(summary.solde)}</div></div>
      </div>
      <table>
        <thead><tr>
          <th>Date</th><th>Type</th><th>Référence</th><th>Libellé</th><th>Statut</th>
          <th style="text-align:right">Débit (DT)</th>
          <th style="text-align:right">Crédit (DT)</th>
          <th style="text-align:right">Solde cumulé</th>
        </tr></thead>
        <tbody>
        ${filteredMovements.map(m => `<tr>
          <td>${m.date || '—'}</td>
          <td>${TYPE_LABELS[m.type]?.label || m.type}</td>
          <td style="font-family:monospace">${m.reference}</td>
          <td>${m.label}</td>
          <td>${m.status || '—'}</td>
          <td class="debit" style="text-align:right">${m.debit > 0 ? fmt(m.debit) : '—'}</td>
          <td class="credit" style="text-align:right">${m.credit > 0 ? fmt(m.credit) : '—'}</td>
          <td class="${m.balance >= 0 ? 'balance-pos' : 'balance-neg'}" style="text-align:right">${fmt(m.balance)}</td>
        </tr>`).join('')}
        </tbody>
      </table>
      <div class="footer">AccountInTime — État généré le ${new Date().toLocaleString('fr-FR')}</div>
    </body></html>`);
    w.document.close();
    w.print();
  };

  if (loading) return (
    <div className="flex items-center justify-center h-screen">
      <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"/>
    </div>
  );

  if (error) return (
    <div className="p-6">
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      <Button variant="secondary" size="sm" onClick={() => navigate(-1)} className="mt-4">← Retour</Button>
    </div>
  );

  const { supplier, summary } = data;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* HEADER */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-200 transition-colors">
            <Icon name="IconArrowLeft" size={20} className="text-gray-600"/>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Icon name="IconTruck" className="text-blue-600" size={24} />
              {supplier.name}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              État détaillé fournisseur
              {supplier.mf && <Badge variant="gray" className="ml-2">{supplier.mf}</Badge>}
              {supplier.phone && <span className="ml-2 text-gray-400"><Icon name="IconPhone" size={12} className="mr-1" />{supplier.phone}</span>}
              {supplier.email && <span className="ml-2 text-gray-400"><Icon name="IconMail" size={12} className="mr-1" />{supplier.email}</span>}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={loadStatement}>
            <Icon name="IconSync" size={16} className="mr-1" />Rafraîchir
          </Button>
          <Button variant="success" size="sm" onClick={handleExportExcel}>
            <Icon name="IconFileSpreadsheet" size={16} className="mr-1" />Excel
          </Button>
          <Button variant="primary" size="sm" onClick={handlePrint}>
            <Icon name="IconPrinter" size={16} className="mr-1" />Imprimer
          </Button>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-l-4 border-red-400 p-5">
          <div className="text-xs text-gray-500 mb-1">Total factures</div>
          <div className="text-xl font-bold text-red-600">{formatCurrency(summary.totalInvoices)}</div>
          <div className="text-xs text-gray-400 mt-1">Montant total dû</div>
        </Card>
        <Card className="border-l-4 border-green-400 p-5">
          <div className="text-xs text-gray-500 mb-1">Total règlements</div>
          <div className="text-xl font-bold text-green-600">{formatCurrency(summary.totalPayments)}</div>
          <div className="text-xs text-gray-400 mt-1">Paiements effectués</div>
        </Card>
        <Card className="border-l-4 border-purple-400 p-5">
          <div className="text-xs text-gray-500 mb-1">Total RS</div>
          <div className="text-xl font-bold text-purple-600">{formatCurrency(summary.totalRS)}</div>
          <div className="text-xs text-gray-400 mt-1">Retenues à la source</div>
        </Card>
        <Card className={`border-l-4 p-5 ${summary.solde > 0 ? 'border-orange-400' : 'border-green-400'}`}>
          <div className="text-xs text-gray-500 mb-1">Solde dû</div>
          <div className={`text-xl font-bold ${summary.solde > 0 ? 'text-orange-600' : 'text-green-600'}`}>
            {formatCurrency(summary.solde)}
          </div>
          <div className="text-xs text-gray-400 mt-1">{summary.solde > 0 ? 'Reste à payer' : 'Solde apuré'}</div>
        </Card>
      </div>

      {/* TABLEAU MOUVEMENTS */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-bold text-gray-800">Mouvements</h2>
          <div className="flex gap-2">
            <Button variant={showFilters ? 'primary' : 'secondary'} size="sm" onClick={() => setShowFilters(!showFilters)}>
              <Icon name="IconFilter" size={16} className="mr-1" /> Filtres
            </Button>
          </div>
        </div>

        {showFilters && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                <Select value={filters.type} onChange={e => setFilters({...filters, type: e.target.value})}>
                  <option value="">-- Tous --</option>
                  <option value="INVOICE">Factures</option>
                  <option value="PAYMENT">Règlements</option>
                  <option value="RS">Retenues RS</option>
                </Select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date début</label>
                <Input type="date" value={filters.dateFrom} onChange={e => setFilters({...filters, dateFrom: e.target.value})} />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Date fin</label>
                <Input type="date" value={filters.dateTo} onChange={e => setFilters({...filters, dateTo: e.target.value})} />
              </div>
            </div>
            <Button variant="ghost" size="sm" className="mt-3" onClick={() => setFilters({ dateFrom: '', dateTo: '', type: '' })}>
              <Icon name="IconX" size={16} className="mr-1" />Réinitialiser
            </Button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Référence</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Statut</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-red-600 uppercase">Débit (DT)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-green-600 uppercase">Crédit (DT)</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase">Solde cumulé</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredMovements.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-12 text-center text-gray-400">
                    <Icon name="IconInbox" size={48} className="mb-3 block mx-auto opacity-30" />
                    Aucun mouvement
                  </td>
                </tr>
              ) : filteredMovements.map((m, i) => {
                const typeInfo = TYPE_LABELS[m.type] || { label: m.type, variant: 'gray' };
                return (
                  <tr key={i} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 text-sm text-gray-600">{m.date || '—'}</td>
                    <td className="px-4 py-3">
                      <Badge variant={typeInfo.variant}>{typeInfo.label}</Badge>
                    </td>
                    <td className="px-4 py-3 text-xs font-mono text-gray-700">{m.reference}</td>
                    <td className="px-4 py-3">
                      <Badge variant={STATUS_COLORS[m.status] || 'gray'}>{m.status || '—'}</Badge>
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-red-600">
                      {m.debit > 0 ? fmt(m.debit) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right text-sm font-semibold text-green-600">
                      {m.credit > 0 ? fmt(m.credit) : <span className="text-gray-300">—</span>}
                    </td>
                    <td className={`px-4 py-3 text-right text-sm font-bold ${m.balance > 0 ? 'text-orange-600' : m.balance < 0 ? 'text-green-600' : 'text-gray-500'}`}>
                      {fmt(m.balance)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {filteredMovements.length > 0 && (
              <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                <tr>
                  <td colSpan="4" className="px-4 py-3 text-sm font-semibold text-gray-700">
                    {filteredMovements.length} mouvement(s)
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-red-600">
                    {fmt(filteredMovements.reduce((s, m) => s + m.debit, 0))}
                  </td>
                  <td className="px-4 py-3 text-right font-bold text-green-600">
                    {fmt(filteredMovements.reduce((s, m) => s + m.credit, 0))}
                  </td>
                  <td className={`px-4 py-3 text-right font-bold text-lg ${summary.solde > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    {fmt(summary.solde)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </div>
  );
}
